import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

interface Contact {
  id: string;
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedinUrl?: string | null;
  isPrimary?: boolean;
  notes?: string | null;
  employerName?: string | null;
  companyId?: string | null;
  willingToAdvocate?: boolean | null;
  advocateNote?: string | null;
}

interface EditContactDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  /** Show the seller-advocate toggle (contacts at Client companies). */
  allowAdvocate?: boolean;
}

export function EditContactDrawer({ open, onOpenChange, contact, allowAdvocate = false }: EditContactDrawerProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [linkedIn, setLinkedIn] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState("");
  const [willingToAdvocate, setWillingToAdvocate] = useState(false);
  const [advocateNote, setAdvocateNote] = useState("");

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Populate form when contact changes
  useEffect(() => {
    if (contact) {
      const nameParts = (contact.name || "").split(" ");
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
      setEmail(contact.email || "");
      setPhone(contact.phone || "");
      setTitle(contact.title || "");
      setLinkedIn(contact.linkedinUrl || "");
      setIsPrimary(contact.isPrimary || false);
      setNotes(contact.notes || "");
      setWillingToAdvocate(!!contact.willingToAdvocate);
      setAdvocateNote(contact.advocateNote || "");
    }
  }, [contact]);

  const updateContactMutation = useMutation({
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      title?: string;
      linkedinUrl?: string;
      isPrimary?: boolean;
      notes?: string;
      willingToAdvocate?: boolean;
      advocateNote?: string | null;
    }) => {
      const res = await fetch(`/api/contacts/${contact?.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to update contact");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact", contact?.id] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast({ title: "Contact updated", description: "The contact has been updated successfully." });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleSave = () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({ title: "Missing fields", description: "Please enter the contact's first and last name.", variant: "destructive" });
      return;
    }

    updateContactMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      title: title.trim() || undefined,
      linkedinUrl: linkedIn.trim() || undefined,
      isPrimary,
      notes: notes.trim() || undefined,
      ...(allowAdvocate ? { willingToAdvocate, advocateNote: willingToAdvocate ? advocateNote.trim() || null : null } : {}),
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] sm:max-w-[480px] p-0 flex flex-col">
        <SheetHeader className="px-6 py-5 border-b border-border flex-shrink-0">
          <SheetTitle className="text-xl font-semibold">Edit Contact</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>First Name <span className="text-red-500">*</span></Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name <span className="text-red-500">*</span></Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="h-11"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Managing Partner"
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label>LinkedIn</Label>
            <Input
              value={linkedIn}
              onChange={(e) => setLinkedIn(e.target.value)}
              placeholder="https://linkedin.com/in/"
              className="h-11"
            />
          </div>

          <div className="flex items-center space-x-2 py-2">
            <Checkbox
              id="edit-primary"
              checked={isPrimary}
              onCheckedChange={(checked) => setIsPrimary(checked as boolean)}
              className="border-primary data-[state=checked]:bg-primary"
            />
            <Label htmlFor="edit-primary" className="font-normal cursor-pointer">
              Set as primary contact for this company
            </Label>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px] resize-none"
            />
          </div>

          {allowAdvocate && (
            <div className="rounded-lg border p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="rounded" checked={willingToAdvocate} onChange={(e) => setWillingToAdvocate(e.target.checked)} />
                Willing to advocate
              </label>
              <p className="text-xs text-muted-foreground">A former client who will take a call from a nervous owner. A badge and a source type for attribution, nothing more.</p>
              {willingToAdvocate && (
                <Input value={advocateNote} onChange={(e) => setAdvocateNote(e.target.value)} placeholder="Speaking now; ask after close; sectors they will speak to" />
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex-shrink-0 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={updateContactMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateContactMutation.isPending || !firstName.trim() || !lastName.trim()}
            className="gold-gradient text-primary-foreground font-semibold btn-gold"
          >
            {updateContactMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
